"use client";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import AlertDialog from "./AlertDialog";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import AssignedProjectsPicker from "./AssignedProjectsModal";
// ─── Design tokens ────────────────────────────────────────────────────────────
const ROLE_LABELS = {
  admin: "Admin",
  foreman: "Foreman",
  project_manager: "Project Manager",
  site_worker: "Site Worker",
  estimator: "Estimator",
  unassigned: "Unassigned",
};
const ROLE_COLORS = {
  admin: "bg-orange-400/15 text-orange-300 ring-orange-400/30",
  foreman: "bg-blue-400/15 text-blue-300 ring-blue-400/30",
  project_manager: "bg-purple-400/15 text-purple-300 ring-purple-400/30",
  site_worker: "bg-teal-400/15 text-teal-300 ring-teal-400/30",
  estimator: "bg-yellow-400/15 text-yellow-300 ring-yellow-400/30",
  unassigned: "bg-gray-400/15 text-gray-400 ring-gray-400/30",
};
const CLASSIFICATION_COLORS = {
  apprentice: "bg-sky-400/10 text-sky-300 ring-sky-400/20",
  journeyman: "bg-cyan-400/10 text-cyan-300 ring-cyan-400/20",
  foreman: "bg-indigo-400/10 text-indigo-300 ring-indigo-400/20",
};

// ─── Primitives ───────────────────────────────────────────────────────────────
const SectionHeader = ({ title, icon }) => (
  <div className="flex items-center gap-3 mb-4 mt-2">
    <span className="text-xs font-semibold text-orange-400 uppercase tracking-widest flex items-center gap-1.5">
      {icon && (
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={icon}
          />
        </svg>
      )}
      {title}
    </span>
    <div className="flex-1 h-px bg-gray-700" />
  </div>
);
const FieldLabel = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
    {children}
  </label>
);
const FieldValue = ({ children, muted }) => (
  <p
    className={`text-sm font-medium ${muted ? "text-gray-500 italic" : "text-gray-100"}`}
  >
    {children ?? (
      <span className="text-gray-600 not-italic font-normal">—</span>
    )}
  </p>
);
const inputClass =
  "block w-full rounded-lg bg-gray-700 border border-gray-600 text-gray-100 text-sm px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors placeholder-gray-500";
const selectClass =
  "block w-full rounded-lg bg-gray-700 border border-gray-600 text-gray-100 text-sm px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors";
const Toggle = ({ value, onToggle, label, disabled }) => (
  <label className="flex items-center justify-between cursor-pointer group">
    <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
      {label}
    </span>
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled || !onToggle}
      className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${value ? "bg-orange-400" : "bg-gray-600"}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${value ? "translate-x-5" : "translate-x-0"}`}
      />
    </button>
  </label>
);
const StatusBadge = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ring-1 ring-inset ${active ? "bg-green-400/10 text-green-400 ring-green-400/20" : "bg-red-400/10 text-red-400 ring-red-400/20"}`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${active ? "bg-green-400" : "bg-red-400"}`}
    />
    {active ? "Active" : "Inactive"}
  </span>
);
const InviteBadge = ({ status }) => {
  const map = {
    Sent: "bg-blue-400/10 text-blue-300 ring-blue-400/20",
    "Not Sent": "bg-gray-400/10 text-gray-400 ring-gray-400/20",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ring-1 ring-inset ${map[status] || map["Not Sent"]}`}
    >
      {status || "Not Sent"}
    </span>
  );
};
const MissingBadge = ({ label }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-400/10 text-amber-300 ring-1 ring-inset ring-amber-400/25">
    <svg
      className="w-3 h-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
      />
    </svg>
    {label}
  </span>
);

// ─── Document card ────────────────────────────────────────────────────────────
function DocCard({
  doc,
  onEdit,
  onArchive,
  onUnarchive,
  isEditingThis,
  editedDoc,
  onEditChange,
  onSave,
  onCancelEdit,
  editFileInputRef,
}) {
  const isExpired = new Date(doc.expiryDate) <= new Date();
  const isArchived = doc.archived;
  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${isArchived ? "bg-gray-800/40 border-gray-700/50 opacity-60" : isExpired ? "bg-red-400/5 border-red-400/20" : "bg-gray-700/40 border-gray-600/50"}`}
    >
      {isEditingThis ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <FieldLabel>Document Name</FieldLabel>
              <input
                type="text"
                name="name"
                value={editedDoc.name}
                onChange={onEditChange}
                className={inputClass}
                placeholder="e.g., First Aid Certificate"
              />
            </div>
            <div>
              <FieldLabel>Date Completed</FieldLabel>
              <input
                type="date"
                name="completedDate"
                value={editedDoc.completedDate}
                onChange={onEditChange}
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <FieldLabel>Replace File (optional)</FieldLabel>
            <input
              type="file"
              name="file"
              ref={editFileInputRef}
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={onEditChange}
              className="hidden"
            />
            <button
              onClick={() => editFileInputRef.current?.click()}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-700 border border-gray-600 text-sm text-gray-300 hover:bg-gray-600 transition-colors"
            >
              <span className="truncate">
                {editedDoc.file?.name || "Choose new file…"}
              </span>
              <svg
                className="w-4 h-4 text-gray-500 shrink-0 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
            </button>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onCancelEdit}
              className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors"
            >
              Save Changes
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isExpired ? "bg-red-400/10" : "bg-green-400/10"}`}
            >
              <svg
                className={`w-4 h-4 ${isExpired ? "text-red-400" : "text-green-400"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-100 truncate">
                {doc.name}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Completed{" "}
                {new Date(doc.completedDate).toLocaleDateString("en-CA", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
                {" · "}
                Expires{" "}
                {new Date(doc.expiryDate).toLocaleDateString("en-CA", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </p>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ring-1 ring-inset ${isExpired ? "bg-red-400/10 text-red-300 ring-red-400/20" : "bg-green-400/10 text-green-300 ring-green-400/20"}`}
                >
                  {isExpired ? "Expired" : "Valid"}
                </span>
                {isArchived && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ring-1 ring-inset bg-gray-400/10 text-gray-400 ring-gray-400/20">
                    Archived
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2.5 py-1 text-xs font-medium text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 transition-colors"
            >
              View
            </a>
            {!isArchived && (
              <button
                onClick={() => onEdit(doc)}
                className="px-2.5 py-1 text-xs font-medium text-orange-300 hover:text-white bg-orange-400/10 hover:bg-orange-400/20 rounded-lg border border-orange-400/20 transition-colors"
              >
                Edit
              </button>
            )}
            <button
              onClick={() =>
                isArchived ? onUnarchive(doc._id) : onArchive(doc._id)
              }
              className="px-2.5 py-1 text-xs font-medium text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 transition-colors"
            >
              {isArchived ? "Restore" : "Archive"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function UserProfile() {
  const [user, setUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [unions, setUnions] = useState([]);
  const [laborRates, setLaborRates] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isPayTypeOpen, setIsPayTypeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isDocUploadOpen, setIsDocUploadOpen] = useState(false);
  const [docUpload, setDocUpload] = useState({
    docTypeId: "",
    completedDate: "",
    file: null,
  });

  const [editingDocId, setEditingDocId] = useState(null);
  const [editedDoc, setEditedDoc] = useState({
    name: "",
    expiryDate: "",
    file: null,
  });
  const navigate = useNavigate();
  const { userId } = useParams();
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const isProjectManager = currentUser?.role === "project_manager";

  const [dialog, setDialog] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
    showCopyButton: false,
  });
  const [safetyDocTypes, setSafetyDocTypes] = useState([]);
  const placesStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#374151",
      borderColor: state.isFocused ? "#f97316" : "#4b5563",
      boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
      "&:hover": { borderColor: "#f97316" },
      borderRadius: "0.5rem",
      padding: "2px",
      fontSize: "14px",
      color: "#f3f4f6",
    }),
    input: (p) => ({ ...p, color: "#f3f4f6", margin: 0, padding: 0 }),
    singleValue: (p) => ({ ...p, color: "#f3f4f6" }),
    placeholder: (p) => ({ ...p, color: "#6b7280" }),
    option: (p, s) => ({
      ...p,
      backgroundColor: s.isSelected ? "#4b5563" : "#374151",
      color: s.isSelected ? "#f97316" : "#f3f4f6",
      "&:hover": { backgroundColor: "#4b5563" },
      fontSize: "13px",
    }),
    menu: (p) => ({ ...p, backgroundColor: "#374151", borderRadius: "0.5rem" }),
    valueContainer: (p) => ({ ...p, padding: "0 4px" }),
    indicatorsContainer: (p) => ({ ...p, padding: "0" }),
    dropdownIndicator: (p) => ({ ...p, padding: "2px", color: "#9ca3af" }),
  };

  const fetchSafetyDocTypes = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile/safety-doc-types",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSafetyDocTypes(res.data.filter((dt) => dt.active));
    } catch {}
  };

  const resolvePlaceDetails = async (place, setAddress) => {
    if (!place?.value) return;
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${place.value.place_id}&key=AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg`,
      );
      const data = await res.json();
      if (data.status === "OK" && data.results.length > 0) {
        const result = data.results[0];
        const { lat, lng } = result.geometry.location;
        let street = "",
          city = "",
          state = "",
          postalCode = "",
          country = "";
        result.address_components.forEach((c) => {
          if (c.types.includes("street_number") || c.types.includes("route"))
            street += (street ? " " : "") + c.long_name;
          if (c.types.includes("locality")) city = c.long_name;
          if (c.types.includes("administrative_area_level_1"))
            state = c.short_name;
          if (c.types.includes("postal_code")) postalCode = c.long_name;
          if (c.types.includes("country")) country = c.long_name;
        });
        setAddress({
          formattedAddress: place.label,
          street,
          city,
          state,
          postalCode,
          country,
          latitude: lat,
          longitude: lng,
        });
      }
    } catch {}
  };

  const unionOptions = [
    { value: "non-union", label: "Non-Union" },
    ...unions.map((u) => ({ value: u.title, label: u.title })),
  ];
  const payTypeOptions = [
    { value: "pieceworker", label: "Pieceworker" },
    { value: "hourly", label: "Hourly" },
    { value: "subcontractor", label: "Subcontractor" },
    { value: "salary", label: "Salary" },
  ];
  const getPayTypeOptions = (isTemporary) =>
    isTemporary
      ? payTypeOptions.filter(
          (o) => o.value === "hourly" || o.value === "pieceworker",
        )
      : payTypeOptions;

  const isSiteOrForeman = (role) => ["site_worker", "foreman"].includes(role);

  // ── Fetching ───────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchUser();
    fetchAccounts();
    fetchUnions();
    fetchLaborRates();
    fetchSafetyDocTypes();
    fetchAllProjects();
  }, [userId]);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/users/me",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCurrentUser(res.data.user);
    };
    init();
  }, []);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUser({
        ...res.data,
        healthAndSafetyDocs: res.data.healthAndSafetyDocs || [],
      });
    } catch {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to load user.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } finally {
      setLoading(false);
    }
  };
  const fetchAllProjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/mobile/projects/select-list?limit=200",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAllProjects(res.data.projects || []);
    } catch {}
  };

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/accounts",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAccounts(res.data);
    } catch {}
  };

  const fetchUnions = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile/unions",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUnions(res.data);
    } catch {}
  };

  const fetchLaborRates = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile/labor-rates",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLaborRates(
        Array.isArray(res.data.laborRates) ? res.data.laborRates : [],
      );
    } catch (err) {
      console.error("Failed to fetch labor rates:", err);
      setLaborRates([]);
    }
  };

  // ── Edit handlers ──────────────────────────────────────────────────────────
  const handleEdit = () => {
    setEditingUser({
      ...user,
      payType: Array.isArray(user.payType)
        ? user.payType
        : user.payType
          ? [user.payType]
          : [],
      account: user.account?._id || null,
      hoursPerWeek:
        user.hoursPerWeek || (user.payType?.includes?.("salary") ? 40 : null),
      collectsTax: user.collectsTax || false,
      taxRate: user.taxRate || 0,
      wsib: user.wsib !== undefined ? user.wsib : true,
      paymentType: user.paymentType || "contractor",
      phoneNumber: user.phoneNumber || "",
      workerClassification: user.workerClassification
        ? user.workerClassification.toLowerCase()
        : null,
      assignedProjects: (user.assignedProjects || []).map((p) =>
        typeof p === "string" ? p : p._id,
      ),
      payrollInfo: user.payrollInfo || {
        companyName: "",
        taxNumber: "",
        voidCheckNumber: "",
        address: {
          formattedAddress: "",
          street: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
          latitude: null,
          longitude: null,
        },
      },
    });
    setIsEditing(true);
  };

  const handlePayrollChange = (e) => {
    const { name, value } = e.target;
    setEditingUser((prev) => ({
      ...prev,
      payrollInfo: { ...prev.payrollInfo, [name]: value },
    }));
  };

  const toggleAssignedProject = (projectId) => {
    setEditingUser((prev) => {
      const current = prev.assignedProjects || [];
      return {
        ...prev,
        assignedProjects: current.includes(projectId)
          ? current.filter((id) => id !== projectId)
          : [...current, projectId],
      };
    });
  };

  const clearAssignedProjects = () => {
    setEditingUser((prev) => ({ ...prev, assignedProjects: [] }));
  };

  const handlePayrollAddressSelect = async (place) => {
    await resolvePlaceDetails(place, (address) => {
      setEditingUser((prev) => ({
        ...prev,
        payrollInfo: { ...prev.payrollInfo, address },
      }));
    });
  };

  const handleToggle = (field) => () => {
    setEditingUser((prev) => {
      const updated = { ...prev, [field]: !prev[field] };
      if (field === "isTemporary" && updated.isTemporary)
        updated.payType = updated.payType.filter(
          (t) => t !== "subcontractor" && t !== "salary",
        );
      if (field === "isTemporary" && !updated.isTemporary)
        updated.workerClassification = null;
      return updated;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditingUser((prev) => {
      let updated = {
        ...prev,
        [name]:
          name === "payRate" || name === "hoursPerWeek" || name === "taxRate"
            ? parseFloat(value) || 0
            : name === "workerClassification" && value === ""
              ? null
              : name === "workerClassification"
                ? value.toLowerCase()
                : value,
      };

      // Auto-fill from Labor Rate Classifications
      if (name === "workerClassification" && value && laborRates.length > 0) {
        const rateEntry = laborRates.find(
          (r) => r.name?.trim().toLowerCase() === value.trim().toLowerCase(),
        );
        if (rateEntry) {
          if (rateEntry.hourlyRate > 0) updated.payRate = rateEntry.hourlyRate;
          if (rateEntry.isUnion && rateEntry.unionName) {
            updated.union = rateEntry.unionName;
            updated.level = rateEntry.unionLevel || "1";
          }
        }
      }

      // Clear classification fields if role changes away from site_worker/foreman
      if (name === "role" && !isSiteOrForeman(value)) {
        updated.workerClassification = null;
        // Payment type only applies to site workers
        if (name === "role" && value !== "site_worker") {
          updated.paymentType = "contractor";
        }
        updated.union = "non-union";
        updated.level = "1";
      }

      return updated;
    });
  };
  const handlePayTypeToggle = (val) => {
    setEditingUser((prev) => {
      if (prev.isTemporary && val === "subcontractor") return prev;
      const next = prev.payType.includes(val)
        ? prev.payType.filter((t) => t !== val)
        : [...prev.payType, val];
      return {
        ...prev,
        payType: next,
        account: next.includes("subcontractor") ? prev.account : null,
        collectsTax: next.includes("pieceworker") ? prev.collectsTax : false,
        taxRate: next.includes("pieceworker") ? prev.taxRate : 0,
        hoursPerWeek:
          next.includes("hourly") || next.includes("salary")
            ? prev.hoursPerWeek || 40
            : null,
      };
    });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const payload = { ...editingUser };
      if (!payload.payType.includes("subcontractor")) delete payload.account;
      delete payload.assignedProjects; // saved via its own endpoint below

      const res = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/users/${userId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      let savedUser = res.data.user;

      if (isSiteOrForeman(savedUser.role)) {
        try {
          const projRes = await axios.put(
            `https://erp-production-72da01c8e651.herokuapp.com/api/users/${userId}/assigned-projects`,
            { projectIds: editingUser.assignedProjects || [] },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          savedUser = {
            ...savedUser,
            assignedProjects: projRes.data.assignedProjects || [],
          };
        } catch (projErr) {
          console.error("Failed to save project assignments:", projErr);
        }
      }

      setUser(savedUser);
      setIsEditing(false);
      setDialog({
        open: true,
        type: "success",
        title: "Saved",
        message: "User profile updated.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to save.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  // ── Invite & password (unchanged) ──────────────────────────────────────────
  const handleInvite = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/users/${userId}/invite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUser((prev) => ({
        ...prev,
        invitationStatus: "Sent",
        invitationLastSent: new Date().toISOString(),
      }));
      setDialog({
        open: true,
        type: "success",
        title: "Invitation Sent",
        message: "Invitation sent successfully!",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to send invitation.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleRegeneratePassword = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/auth/regenerate-temp-password",
        { userId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDialog({
        open: true,
        type: "success",
        title: "New Password Generated",
        message: `Password: ${res.data.temporaryPassword}`,
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: true,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to regenerate.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleCopyToClipboard = async () => {
    const pw = dialog.message.match(/Password: (\S+)/)?.[1];
    if (pw) await navigator.clipboard.writeText(pw).catch(() => {});
  };

  // ── Document handlers (unchanged) ──────────────────────────────────────────
  const handleDocChange = (e) => {
    const { name, value, files } = e.target;
    setDocUpload((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };
  const handleDocChangeEdit = (e) => {
    const { name, value, files } = e.target;
    setEditedDoc((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };
  const handleDocUpload = async () => {
    if (!docUpload.docTypeId || !docUpload.completedDate || !docUpload.file) {
      setDialog({
        open: true,
        type: "error",
        title: "Missing Fields",
        message: "Please select a document type, completion date, and file.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
      return;
    }
    const fd = new FormData();
    fd.append("document", docUpload.file);
    fd.append("docTypeId", docUpload.docTypeId);
    fd.append("completedDate", docUpload.completedDate);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/users/${userId}/upload-doc`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      setUser((prev) => ({
        ...prev,
        healthAndSafetyDocs: [
          ...(prev.healthAndSafetyDocs || []),
          res.data.doc,
        ],
      }));
      setDocUpload({ docTypeId: "", completedDate: "", file: null });
      setIsDocUploadOpen(false);
      setDialog({
        open: true,
        type: "success",
        title: "Uploaded",
        message: "Document uploaded.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Upload failed.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    }
  };
  // With this:
  const handleEditDoc = (doc) => {
    setEditingDocId(doc._id);
    setEditedDoc({
      completedDate: doc.completedDate ? doc.completedDate.split("T")[0] : "",
      file: null,
    });
  };
  const handleSaveDoc = async (docId) => {
    if (!editedDoc.completedDate) return;
    const fd = new FormData();
    fd.append("completedDate", editedDoc.completedDate);
    if (editedDoc.file) fd.append("document", editedDoc.file);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/users/${userId}/documents/${docId}`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      setUser((prev) => ({
        ...prev,
        healthAndSafetyDocs: prev.healthAndSafetyDocs.map((d) =>
          d._id === docId ? res.data.doc : d,
        ),
      }));
      setEditingDocId(null);
      setEditedDoc({ completedDate: "", file: null });
      setDialog({
        open: true,
        type: "success",
        title: "Updated",
        message: "Document updated.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Update failed.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    }
  };
  const handleArchiveDoc = async (docId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/users/${userId}/documents/${docId}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUser((prev) => ({
        ...prev,
        healthAndSafetyDocs: prev.healthAndSafetyDocs.map((d) =>
          d._id === docId
            ? { ...d, archived: true, archivedAt: new Date().toISOString() }
            : d,
        ),
      }));
    } catch {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to archive.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    }
  };
  const handleUnarchiveDoc = async (docId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/users/${userId}/documents/${docId}/unarchive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUser((prev) => ({
        ...prev,
        healthAndSafetyDocs: prev.healthAndSafetyDocs.map((d) =>
          d._id === docId ? { ...d, archived: false, archivedAt: null } : d,
        ),
      }));
    } catch {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to restore.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-400">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span className="text-sm">Loading profile…</span>
        </div>
      </div>
    );
  }
  if (!user)
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-gray-500">User not found.</p>
      </div>
    );

  // ── Derived state ─────────────────────────────────────────────────────────
  const displayUser = isEditing ? editingUser : user;
  const isMissingPayRate =
    Array.isArray(user.payType) &&
    user.payType.includes("hourly") &&
    (!user.payRate || user.payRate === 0);
  const isMissingEmpNo = !user.employeeNumber;
  const activeDocs = (user.healthAndSafetyDocs || []).filter(
    (d) => !d.archived,
  );
  const archivedDocs = (user.healthAndSafetyDocs || []).filter(
    (d) => d.archived,
  );
  const expiredActiveDocs = activeDocs.filter(
    (d) => new Date(d.expiryDate) <= new Date(),
  );
  const safeToWork = activeDocs.length > 0 && expiredActiveDocs.length === 0;

  const showClassification = isEditing
    ? isSiteOrForeman(editingUser.role)
    : isSiteOrForeman(user.role);
  const showUnion = isEditing
    ? isSiteOrForeman(editingUser.role)
    : isSiteOrForeman(user.role);
  const showLevel =
    showUnion &&
    (isEditing
      ? editingUser.union !== "non-union"
      : user.union !== "non-union");

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-900 text-white mt-4 m-4 rounded-lg ">
      <AlertDialog
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText="OK"
        cancelText="Close"
        open={dialog.open}
        onClose={() => setDialog((p) => ({ ...p, open: false }))}
        onConfirm={dialog.onConfirm}
        showCopyButton={dialog.showCopyButton}
        onCopy={handleCopyToClipboard}
      />

      <div className="container mx-auto px-4 py-8">
        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/users")}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors group"
          >
            <svg
              className="w-4 h-4 transition-transform group-hover:-translate-x-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Crew
          </button>
          <div className="flex items-center gap-2">
            {!isProjectManager && !isEditing && (
              <>
                {user.isTemporary && (
                  <button
                    onClick={handleRegeneratePassword}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Regen Password
                  </button>
                )}
                <button
                  onClick={handleInvite}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Send Invite
                </button>
                <button
                  onClick={handleEdit}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors shadow-sm"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Edit Profile
                </button>
              </>
            )}
            {isEditing && (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditingUser(null);
                  }}
                  className="px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors shadow-sm"
                >
                  Save Changes
                </button>
              </>
            )}
          </div>
        </div>

        {/* Hero card */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 mb-5 shadow-lg">
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 rounded-xl bg-gray-700 border border-gray-600 flex items-center justify-center shrink-0 text-2xl font-bold text-gray-300 uppercase">
              {user.displayName?.charAt(0) || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl font-bold text-white truncate">
                  {user.displayName}
                </h1>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ring-1 ring-inset ${ROLE_COLORS[user.role] || ROLE_COLORS.unassigned}`}
                >
                  {ROLE_LABELS[user.role] || user.role}
                </span>
                <StatusBadge active={user.active} />
                {user.isTemporary && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-yellow-400/10 text-yellow-300 ring-1 ring-inset ring-yellow-400/20">
                    Temporary
                  </span>
                )}
                {user.workerClassification && (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ring-1 ring-inset capitalize ${CLASSIFICATION_COLORS[user.workerClassification] || "bg-cyan-400/10 text-cyan-300 ring-cyan-400/20"}`}
                  >
                    {user.workerClassification}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-400 mt-1">{user.email}</p>
              {(isMissingPayRate || isMissingEmpNo) && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {isMissingPayRate && (
                    <MissingBadge label="Pay rate not set" />
                  )}
                  {isMissingEmpNo && (
                    <MissingBadge label="Employee # not set" />
                  )}
                </div>
              )}
            </div>
            <div
              className={`shrink-0 flex flex-col items-center gap-1 px-4 py-2.5 rounded-xl border ${safeToWork ? "border-green-400/20" : "border-red-400/20"}`}
            >
              <svg
                className={`w-5 h-5 ${safeToWork ? "text-green-400" : "text-red-400"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    safeToWork
                      ? "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      : "M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v4m0 4h.01"
                  }
                />
              </svg>
              <span
                className={`text-[10px] font-bold tracking-wide uppercase ${safeToWork ? "text-green-400" : "text-red-400"}`}
              >
                {safeToWork ? "Safe" : "Review"}
              </span>
            </div>
          </div>
        </div>

        {/* 3-col grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left 2/3 */}
          <div className="lg:col-span-2 space-y-5">
            {/* Identity & Contact */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
              <SectionHeader
                title="Identity & Contact"
                icon="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <FieldLabel>Display Name</FieldLabel>
                  {isEditing ? (
                    <input
                      name="displayName"
                      value={editingUser.displayName || ""}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  ) : (
                    <FieldValue>{user.displayName}</FieldValue>
                  )}
                </div>
                <div>
                  <FieldLabel>Email</FieldLabel>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={editingUser.email || ""}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  ) : (
                    <FieldValue>{user.email}</FieldValue>
                  )}
                </div>
                <div>
                  <FieldLabel>Phone Number</FieldLabel>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={editingUser.phoneNumber || ""}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className={inputClass}
                    />
                  ) : (
                    <FieldValue muted={!user.phoneNumber}>
                      {user.phoneNumber || "Not provided"}
                    </FieldValue>
                  )}
                </div>
                <div>
                  <FieldLabel>Employee #</FieldLabel>
                  {isEditing ? (
                    <input
                      name="employeeNumber"
                      value={editingUser.employeeNumber || ""}
                      onChange={handleChange}
                      placeholder="EMP-001"
                      className={inputClass}
                    />
                  ) : (
                    <div className="mt-0.5">
                      {user.employeeNumber ? (
                        <span className="text-sm font-mono text-gray-200 bg-gray-700/60 px-2 py-0.5 rounded border border-gray-600">
                          {user.employeeNumber}
                        </span>
                      ) : (
                        <MissingBadge label="Not set" />
                      )}
                    </div>
                  )}
                </div>
                {(isEditing || user.ripplingEmployeeNumber) && (
                  <div>
                    <FieldLabel>Rippling Employee #</FieldLabel>
                    {isEditing ? (
                      <input
                        name="ripplingEmployeeNumber"
                        value={editingUser.ripplingEmployeeNumber || ""}
                        onChange={handleChange}
                        placeholder="Synced from Rippling"
                        className={inputClass}
                      />
                    ) : (
                      <div className="mt-0.5">
                        <span className="text-sm font-mono text-gray-200 bg-gray-700/60 px-2 py-0.5 rounded border border-gray-600">
                          {user.ripplingEmployeeNumber}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Role & Classification */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
              <SectionHeader
                title="Role & Classification"
                icon="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <FieldLabel>Role</FieldLabel>
                  {isEditing ? (
                    <select
                      name="role"
                      value={editingUser.role || "unassigned"}
                      onChange={handleChange}
                      className={selectClass}
                    >
                      <option value="unassigned">Unassigned</option>
                      {editingUser.isTemporary ? (
                        <>
                          <option value="site_worker">Site Worker</option>
                          <option value="foreman">Foreman</option>
                        </>
                      ) : (
                        <>
                          <option value="admin">Admin</option>
                          <option value="foreman">Foreman</option>
                          <option value="project_manager">
                            Project Manager
                          </option>
                          <option value="site_worker">Site Worker</option>
                          <option value="estimator">Estimator</option>
                        </>
                      )}
                    </select>
                  ) : (
                    <div className="mt-0.5">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ring-1 ring-inset ${ROLE_COLORS[user.role] || ROLE_COLORS.unassigned}`}
                      >
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </div>
                  )}
                </div>

                {showClassification && (
                  <div>
                    <FieldLabel>Worker Classification</FieldLabel>
                    {isEditing ? (
                      <select
                        name="workerClassification"
                        value={editingUser.workerClassification || ""}
                        onChange={handleChange}
                        className={selectClass}
                      >
                        <option value="">Select classification...</option>
                        {laborRates.map((rate) => (
                          <option
                            key={rate._id || rate.name}
                            value={rate.name.toLowerCase()}
                          >
                            {rate.name}
                            {rate.isUnion && rate.unionName
                              ? ` (${rate.unionName})`
                              : rate.isUnion
                                ? " (Union)"
                                : ""}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="mt-0.5">
                        {user.workerClassification ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ring-1 ring-inset capitalize ${CLASSIFICATION_COLORS[user.workerClassification] || "bg-cyan-400/10 text-cyan-300 ring-cyan-400/20"}`}
                          >
                            {user.workerClassification}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-600">—</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {showUnion && (
                  <div>
                    <FieldLabel>Union</FieldLabel>
                    {isEditing ? (
                      <select
                        name="union"
                        value={editingUser.union || "non-union"}
                        onChange={handleChange}
                        className={selectClass}
                      >
                        {unionOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <FieldValue>
                        {unionOptions.find((o) => o.value === user.union)
                          ?.label ||
                          user.union ||
                          "Non-Union"}
                      </FieldValue>
                    )}
                  </div>
                )}

                {showLevel && (
                  <div>
                    <FieldLabel>Level</FieldLabel>
                    {isEditing ? (
                      <select
                        name="level"
                        value={editingUser.level || "1"}
                        onChange={handleChange}
                        className={selectClass}
                      >
                        {["0", "1", "2", "3", "4"].map((l) => (
                          <option key={l} value={l}>
                            Level {l}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <FieldValue>Level {user.level || "1"}</FieldValue>
                    )}
                  </div>
                )}

                {(isEditing
                  ? editingUser.role === "site_worker"
                  : user.role === "site_worker") && (
                  <div>
                    <FieldLabel>Payment Type</FieldLabel>
                    {isEditing ? (
                      <select
                        name="paymentType"
                        value={editingUser.paymentType || "contractor"}
                        onChange={handleChange}
                        className={selectClass}
                      >
                        <option value="contractor">Contractor</option>
                        <option value="hourly">Hourly</option>
                      </select>
                    ) : (
                      <div className="mt-0.5">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ring-1 ring-inset capitalize ${
                            user.paymentType === "hourly"
                              ? "bg-orange-400/15 text-orange-300 ring-orange-400/30"
                              : "bg-gray-400/15 text-gray-300 ring-gray-400/30"
                          }`}
                        >
                          {user.paymentType || "contractor"}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Compensation */}
            {!isProjectManager && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
                <SectionHeader
                  title="Compensation"
                  icon="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel>Pay Rate</FieldLabel>
                    {isEditing ? (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          $
                        </span>
                        <input
                          type="number"
                          name="payRate"
                          value={editingUser.payRate || 0}
                          onChange={handleChange}
                          step="0.01"
                          className={`${inputClass} pl-7`}
                        />
                      </div>
                    ) : (
                      <div className="mt-0.5">
                        {isMissingPayRate ? (
                          <MissingBadge label="Not set" />
                        ) : (
                          <FieldValue>
                            ${user.payRate?.toFixed(2) || "0.00"}/hr
                          </FieldValue>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <FieldLabel>Pay Type</FieldLabel>
                    {isEditing ? (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsPayTypeOpen(!isPayTypeOpen)}
                          className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-700 border border-gray-600 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-orange-400 transition-colors hover:border-gray-500"
                        >
                          <span
                            className={
                              editingUser.payType.length > 0
                                ? "text-gray-100"
                                : "text-gray-500"
                            }
                          >
                            {editingUser.payType.length > 0
                              ? editingUser.payType
                                  .map(
                                    (t) =>
                                      t.charAt(0).toUpperCase() + t.slice(1),
                                  )
                                  .join(", ")
                              : "Select types"}
                          </span>
                          <svg
                            className={`w-4 h-4 text-gray-400 transition-transform ${isPayTypeOpen ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                        {isPayTypeOpen && (
                          <div className="absolute z-20 w-full mt-1 bg-gray-700 rounded-lg shadow-xl border border-gray-600 overflow-hidden">
                            {getPayTypeOptions(editingUser.isTemporary).map(
                              (opt) => (
                                <label
                                  key={opt.value}
                                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-600 cursor-pointer transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={editingUser.payType.includes(
                                      opt.value,
                                    )}
                                    onChange={() =>
                                      handlePayTypeToggle(opt.value)
                                    }
                                    className="w-4 h-4 accent-orange-400 bg-gray-800 border-gray-600 rounded"
                                  />
                                  <span className="text-sm text-gray-200">
                                    {opt.label}
                                  </span>
                                </label>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {user.payType?.length > 0 ? (
                          user.payType.map((pt) => (
                            <span
                              key={pt}
                              className="px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-300 border border-gray-600 capitalize"
                            >
                              {pt}
                            </span>
                          ))
                        ) : (
                          <FieldValue muted>Not set</FieldValue>
                        )}
                      </div>
                    )}
                  </div>
                  {(isEditing
                    ? editingUser.payType.includes("subcontractor")
                    : user.payType?.includes("subcontractor")) && (
                    <div>
                      <FieldLabel>Account</FieldLabel>
                      {isEditing ? (
                        <select
                          name="account"
                          value={editingUser.account || ""}
                          onChange={handleChange}
                          className={selectClass}
                        >
                          <option value="">Select account</option>
                          {accounts.map((a) => (
                            <option key={a._id} value={a._id}>
                              {a.companyName}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <FieldValue muted={!user.account?.companyName}>
                          {user.account?.companyName || "Not assigned"}
                        </FieldValue>
                      )}
                    </div>
                  )}
                  {(isEditing
                    ? editingUser.payType.includes("salary")
                    : user.payType?.includes("salary")) && (
                    <div>
                      <FieldLabel>Hours / Week</FieldLabel>
                      {isEditing ? (
                        <input
                          type="number"
                          name="hoursPerWeek"
                          value={editingUser.hoursPerWeek || 40}
                          onChange={handleChange}
                          step="0.5"
                          min="0"
                          className={inputClass}
                        />
                      ) : (
                        <FieldValue>{user.hoursPerWeek || 40} hrs</FieldValue>
                      )}
                    </div>
                  )}
                  {(isEditing
                    ? editingUser.payType.includes("pieceworker")
                    : user.payType?.includes("pieceworker")) && (
                    <div className="sm:col-span-2 bg-gray-700/40 rounded-lg p-4 border border-gray-700 space-y-3">
                      <Toggle
                        value={
                          isEditing ? editingUser.collectsTax : user.collectsTax
                        }
                        onToggle={
                          isEditing ? handleToggle("collectsTax") : null
                        }
                        label="Collects Tax"
                        disabled={!isEditing}
                      />
                      {(isEditing
                        ? editingUser.collectsTax
                        : user.collectsTax) && (
                        <div>
                          <FieldLabel>Tax Rate (%)</FieldLabel>
                          {isEditing ? (
                            <input
                              type="number"
                              name="taxRate"
                              value={editingUser.taxRate || 0}
                              onChange={handleChange}
                              step="0.01"
                              min="0"
                              max="100"
                              className={inputClass}
                            />
                          ) : (
                            <FieldValue>
                              {user.taxRate?.toFixed(2) || "0.00"}%
                            </FieldValue>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right 1/3 */}
          <div className="space-y-5">
            {/* Settings */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
              <SectionHeader
                title="Settings"
                icon="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <div className="space-y-4">
                <Toggle
                  value={displayUser.active}
                  onToggle={isEditing ? handleToggle("active") : null}
                  label="Active"
                  disabled={!isEditing}
                />
                <div className="h-px bg-gray-700" />
                <Toggle
                  value={displayUser.wsib}
                  onToggle={isEditing ? handleToggle("wsib") : null}
                  label="WSIB"
                  disabled={!isEditing}
                />
                <div className="h-px bg-gray-700" />
                <Toggle
                  value={displayUser.isTemporary}
                  onToggle={isEditing ? handleToggle("isTemporary") : null}
                  label="Temporary User"
                  disabled={!isEditing}
                />
                <div className="h-px bg-gray-700" />
                <Toggle
                  value={displayUser.geoFence}
                  onToggle={isEditing ? handleToggle("geoFence") : null}
                  label="GeoFence"
                  disabled={!isEditing}
                />
              </div>
            </div>

            {isSiteOrForeman(displayUser.role) && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
                <SectionHeader
                  title="Project Access"
                  icon="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
                {isEditing ? (
                  <AssignedProjectsPicker
                    allProjects={allProjects}
                    selectedIds={editingUser.assignedProjects || []}
                    onToggle={toggleAssignedProject}
                    onClear={clearAssignedProjects}
                  />
                ) : (
                  <div>
                    {(user.assignedProjects || []).length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No restriction — this user can see all active projects.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        <p className="text-xs text-gray-500 mb-2">
                          Restricted to {user.assignedProjects.length} project
                          {user.assignedProjects.length !== 1 ? "s" : ""}
                        </p>
                        {user.assignedProjects.map((p) => (
                          <div
                            key={typeof p === "string" ? p : p._id}
                            className="px-3 py-2 rounded-lg bg-gray-700/40 border border-gray-600/50 text-sm text-gray-200"
                          >
                            {typeof p === "string"
                              ? p
                              : p.projectName || p.projectNumber || p._id}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Invitation */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
              <SectionHeader
                title="Invitation"
                icon="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
              <div className="space-y-3">
                <div>
                  <FieldLabel>Status</FieldLabel>
                  <InviteBadge status={user.invitationStatus} />
                </div>
                <div>
                  <FieldLabel>Last Sent</FieldLabel>
                  <FieldValue muted={!user.invitationLastSent}>
                    {user.invitationLastSent
                      ? new Date(user.invitationLastSent).toLocaleString(
                          "en-CA",
                          { dateStyle: "medium", timeStyle: "short" },
                        )
                      : "Never"}
                  </FieldValue>
                </div>
              </div>
            </div>

            {/* Activity */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
              <SectionHeader
                title="Activity"
                icon="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
              <div>
                <FieldLabel>Last Login</FieldLabel>
                <FieldValue muted={!user.lastLogin}>
                  {user.lastLogin
                    ? new Date(user.lastLogin).toLocaleString("en-CA", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })
                    : "Never logged in"}
                </FieldValue>
              </div>
            </div>
          </div>
        </div>

        {/* Health & Safety Documents */}
        <div className="mt-5 bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-semibold text-orange-400 uppercase tracking-widest">
                Health &amp; Safety Documents
              </span>
              <div className="h-px bg-gray-700 w-10" />
              {activeDocs.length > 0 && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ring-1 ring-inset ${safeToWork ? "bg-green-400/10 text-green-300 ring-green-400/20" : "bg-red-400/10 text-red-300 ring-red-400/20"}`}
                >
                  {activeDocs.length} active · {expiredActiveDocs.length}{" "}
                  expired
                </span>
              )}
            </div>
            <button
              onClick={() => setIsDocUploadOpen(!isDocUploadOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors shadow-sm"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Document
            </button>
          </div>
          {isDocUploadOpen && (
            <div className="mb-5 bg-gray-700/40 border border-gray-600/50 rounded-xl p-5">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                Upload New Document
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <FieldLabel>Document Type</FieldLabel>
                  <select
                    value={docUpload.docTypeId}
                    onChange={(e) =>
                      setDocUpload((p) => ({ ...p, docTypeId: e.target.value }))
                    }
                    className={selectClass}
                  >
                    <option value="">Select type...</option>
                    {safetyDocTypes.map((dt) => (
                      <option key={dt._id} value={dt._id}>
                        {dt.name}
                        {dt.mandatory ? " ★" : ""}
                        {" — "}
                        {dt.durationMonths >= 12 && dt.durationMonths % 12 === 0
                          ? `${dt.durationMonths / 12}yr`
                          : `${dt.durationMonths}mo`}
                      </option>
                    ))}
                  </select>
                  {docUpload.docTypeId &&
                    (() => {
                      const dt = safetyDocTypes.find(
                        (d) => d._id === docUpload.docTypeId,
                      );
                      return dt ? (
                        <p className="text-xs text-gray-500 mt-1">
                          Valid for {dt.durationMonths} months
                          {dt.mandatory && (
                            <span className="text-orange-400 ml-1">
                              · Mandatory
                            </span>
                          )}
                        </p>
                      ) : null;
                    })()}
                </div>
                <div>
                  <FieldLabel>Date Completed / Enrolled</FieldLabel>
                  <input
                    type="date"
                    value={docUpload.completedDate}
                    onChange={(e) =>
                      setDocUpload((p) => ({
                        ...p,
                        completedDate: e.target.value,
                      }))
                    }
                    className={inputClass}
                  />
                  {docUpload.completedDate &&
                    docUpload.docTypeId &&
                    (() => {
                      const dt = safetyDocTypes.find(
                        (d) => d._id === docUpload.docTypeId,
                      );
                      if (!dt) return null;
                      const [y, m, d] = docUpload.completedDate
                        .split("-")
                        .map(Number);
                      const expiry = new Date(y, m - 1, d);
                      expiry.setMonth(expiry.getMonth() + dt.durationMonths);
                      return (
                        <p className="text-xs text-gray-500 mt-1">
                          Expires:{" "}
                          <span className="text-gray-300">
                            {expiry.toLocaleDateString("en-CA", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        </p>
                      );
                    })()}
                </div>
                <div>
                  <FieldLabel>File</FieldLabel>
                  <input
                    type="file"
                    name="file"
                    ref={fileInputRef}
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) =>
                      setDocUpload((p) => ({ ...p, file: e.target.files[0] }))
                    }
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-700 border border-gray-600 text-sm text-gray-300 hover:bg-gray-600 transition-colors"
                  >
                    <span className="truncate">
                      {docUpload.file?.name || "Choose file…"}
                    </span>
                    <svg
                      className="w-4 h-4 text-gray-500 shrink-0 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setIsDocUploadOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDocUpload}
                  className="px-4 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors"
                >
                  Upload
                </button>
              </div>
            </div>
          )}

          {activeDocs.length > 0 ? (
            <div className="space-y-3">
              {activeDocs.map((doc) => (
                <DocCard
                  key={doc._id}
                  doc={doc}
                  onEdit={handleEditDoc}
                  onArchive={handleArchiveDoc}
                  onUnarchive={handleUnarchiveDoc}
                  isEditingThis={editingDocId === doc._id}
                  editedDoc={editedDoc}
                  onEditChange={handleDocChangeEdit}
                  onSave={() => handleSaveDoc(doc._id)}
                  onCancelEdit={() => {
                    setEditingDocId(null);
                    setEditedDoc({ completedDate: "", file: null });
                  }}
                  editFileInputRef={editFileInputRef}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <svg
                className="w-10 h-10 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-sm text-gray-500">
                No documents uploaded yet.
              </p>
            </div>
          )}

          {archivedDocs.length > 0 && (
            <details className="mt-4 group">
              <summary className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-500 hover:text-gray-300 transition-colors list-none select-none">
                <svg
                  className="w-3.5 h-3.5 transition-transform group-open:rotate-90"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                {archivedDocs.length} archived document
                {archivedDocs.length !== 1 ? "s" : ""}
              </summary>
              <div className="mt-3 space-y-3">
                {archivedDocs.map((doc) => (
                  <DocCard
                    key={doc._id}
                    doc={doc}
                    onEdit={handleEditDoc}
                    onArchive={handleArchiveDoc}
                    onUnarchive={handleUnarchiveDoc}
                    isEditingThis={editingDocId === doc._id}
                    editedDoc={editedDoc}
                    onEditChange={handleDocChangeEdit}
                    onSave={() => handleSaveDoc(doc._id)}
                    onCancelEdit={() => {
                      setEditingDocId(null);
                      setEditedDoc({ name: "", expiryDate: "", file: null });
                    }}
                    editFileInputRef={editFileInputRef}
                  />
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Payroll Information */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg mt-4">
          <SectionHeader
            title="Payroll Information"
            icon="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"
          />

          {/* View mode hint */}
          {!isEditing &&
            !user.payrollInfo?.companyName &&
            !user.payrollInfo?.taxNumber && (
              <p className="text-sm text-gray-600 italic">
                No payroll info on file.
              </p>
            )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Company Name */}
            <div>
              <FieldLabel>Company Name</FieldLabel>
              {isEditing ? (
                <input
                  name="companyName"
                  value={editingUser.payrollInfo?.companyName || ""}
                  onChange={handlePayrollChange}
                  placeholder="ABC Holdings Inc."
                  className={inputClass}
                />
              ) : (
                <FieldValue muted={!user.payrollInfo?.companyName}>
                  {user.payrollInfo?.companyName || "Not provided"}
                </FieldValue>
              )}
            </div>

            {/* Tax Number */}
            <div>
              <FieldLabel>Tax # / Business Number</FieldLabel>
              {isEditing ? (
                <input
                  name="taxNumber"
                  value={editingUser.payrollInfo?.taxNumber || ""}
                  onChange={handlePayrollChange}
                  placeholder="123456789 RT0001"
                  className={inputClass}
                />
              ) : (
                <FieldValue muted={!user.payrollInfo?.taxNumber}>
                  {user.payrollInfo?.taxNumber ? (
                    <span className="font-mono text-gray-200 bg-gray-700/60 px-2 py-0.5 rounded border border-gray-600 text-sm">
                      {user.payrollInfo.taxNumber}
                    </span>
                  ) : (
                    "Not provided"
                  )}
                </FieldValue>
              )}
            </div>

            {/* Void Check Number */}
            <div>
              <FieldLabel>Void Check Number</FieldLabel>
              {isEditing ? (
                <input
                  name="voidCheckNumber"
                  value={editingUser.payrollInfo?.voidCheckNumber || ""}
                  onChange={handlePayrollChange}
                  placeholder="e.g., 0042"
                  className={inputClass}
                />
              ) : (
                <FieldValue muted={!user.payrollInfo?.voidCheckNumber}>
                  {user.payrollInfo?.voidCheckNumber ? (
                    <span className="font-mono text-gray-200 bg-gray-700/60 px-2 py-0.5 rounded border border-gray-600 text-sm">
                      {user.payrollInfo.voidCheckNumber}
                    </span>
                  ) : (
                    "Not provided"
                  )}
                </FieldValue>
              )}
            </div>

            {/* Address — full width */}
            <div className="sm:col-span-2">
              <FieldLabel>Address</FieldLabel>
              {isEditing ? (
                <div className="space-y-2">
                  <GooglePlacesAutocomplete
                    apiKey="AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg"
                    selectProps={{
                      onChange: handlePayrollAddressSelect,
                      placeholder: "Search address…",
                      value: editingUser.payrollInfo?.address?.formattedAddress
                        ? {
                            label:
                              editingUser.payrollInfo.address.formattedAddress,
                            value:
                              editingUser.payrollInfo.address.formattedAddress,
                          }
                        : null,
                      styles: placesStyles,
                    }}
                  />
                  {/* Parsed fields for review / manual override */}
                  {editingUser.payrollInfo?.address?.formattedAddress && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {[
                        { key: "street", label: "Street" },
                        { key: "city", label: "City" },
                        { key: "state", label: "Province/State" },
                        { key: "postalCode", label: "Postal Code" },
                        { key: "country", label: "Country" },
                      ].map(({ key, label }) => (
                        <div
                          key={key}
                          className={key === "street" ? "col-span-2" : ""}
                        >
                          <label className="block text-xs text-gray-500 mb-1">
                            {label}
                          </label>
                          <input
                            value={
                              editingUser.payrollInfo?.address?.[key] || ""
                            }
                            onChange={(e) =>
                              setEditingUser((prev) => ({
                                ...prev,
                                payrollInfo: {
                                  ...prev.payrollInfo,
                                  address: {
                                    ...prev.payrollInfo.address,
                                    [key]: e.target.value,
                                  },
                                },
                              }))
                            }
                            className={inputClass}
                            placeholder={label}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <FieldValue
                  muted={!user.payrollInfo?.address?.formattedAddress}
                >
                  {user.payrollInfo?.address?.formattedAddress ||
                    "Not provided"}
                </FieldValue>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
